import { createTheme } from '@mui/material/styles';

/**
 * Hooten Young brand palette — light premium theme.
 *
 * Mirrors the MVP (hydashboard-mvp) so visual continuity is preserved
 * as the production app comes online. Headings in Playfair Display,
 * body in Inter, gold (#d4a84b) as primary brand color, indigo
 * (#4f46e5) as the chart accent.
 */
export const colors = {
  // Brand — gold matches the MVP (hydashboard-mvp/src/theme/theme.js).
  // Cream + near-black are sourced from hootenyoung.com's site palette.
  gold: '#d4a84b',
  goldLight: '#f4bc58',
  goldDark: '#bb8c3f',
  brandCream: '#f9f4ec',
  brandDark: '#2d2d2c',

  // Surfaces
  pageBg: '#f8f9fb',
  cardBg: '#ffffff',
  surfaceHover: '#f3f4f6',
  surfaceSubtle: '#f9fafb',

  // Text — WCAG AA on white
  textPrimary: '#111827',
  textSecondary: '#374151',
  textMuted: '#6b7280',

  // Status
  success: '#059669',
  warning: '#d97706',
  error: '#dc2626',
  info: '#2563eb',

  // Borders
  border: '#e5e7eb',
  borderLight: '#f0f0f0',

  // Tooltip
  tooltipBg: '#1f2937',
  tooltipBorder: 'rgba(0, 0, 0, 0.12)',

  // Charts
  chartPrimary: '#4f46e5',
  chartSecondary: '#2563eb',
  chartTertiary: '#059669',
  chartColors: [
    '#4f46e5',
    '#2563eb',
    '#059669',
    '#d97706',
    '#7c3aed',
    '#0891b2',
    '#ea580c',
    '#db2777',
    '#0d9488',
    '#4f46e5',
    '#dc2626',
  ],

  // Follow-up tracker buckets — one accent per "days since last order"
  // bucket. Centralized so any component that surfaces a bucket label
  // (tracker, simulator, KPI strip) renders the same color.
  bucketColors: {
    '0-30 days': '#059669',
    '30-60 days': '#3b82f6',
    '60-90 days': '#f59e0b',
    '90-120 days': '#f97316',
    '120+ days': '#ef4444',
  },

  // Purchase-velocity frequency categories — driven by active-month
  // count. Used by the heatmap and What-If simulator.
  freqColors: {
    Monthly: '#059669',
    'Bi-Monthly': '#3b82f6',
    Quarterly: '#f59e0b',
    Infrequent: '#ef4444',
  },
} as const;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: colors.gold, light: colors.goldLight, dark: colors.goldDark },
    background: { default: colors.pageBg, paper: colors.cardBg },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
    success: { main: colors.success },
    warning: { main: colors.warning },
    error: { main: colors.error },
    info: { main: colors.info },
    divider: colors.border,
  },
  typography: {
    fontFamily:
      '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontFamily: '"Playfair Display", "Palatino", Georgia, serif', fontWeight: 700 },
    h2: { fontFamily: '"Playfair Display", "Palatino", Georgia, serif', fontWeight: 700 },
    h3: { fontFamily: '"Playfair Display", "Palatino", Georgia, serif', fontWeight: 600 },
    h4: { fontFamily: '"Playfair Display", "Palatino", Georgia, serif', fontWeight: 600 },
    h5: { fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h6: {
      // Section titles across the dashboards. Industry-standard premium
      // analytics treatment: Inter Semibold, slight negative tracking,
      // tight line-height. Inline `fontFamily` overrides have been
      // removed from the components so this theme rule takes effect.
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.25,
      fontSize: '1.0625rem', // 17px — Stripe / Linear section-title scale
    },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.85rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', color: colors.textMuted },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    // GLOBAL TYPOGRAPHY POLISH — industry-standard analytics-dashboard
    // rendering (Stripe, Linear, Notion):
    //   * tabular-nums: every digit gets the same advance width, so
    //     count-up animations don't jitter and columns of numbers in
    //     tables align under each other.
    //   * lining-nums: uniform-height digits (no descenders).
    //   * antialiased smoothing + optimizeLegibility: cleaner on
    //     non-Retina displays.
    //   * Inter is sans for body, Playfair Display for serif headings
    //     (set explicitly at the Typography call site for hero values).
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        body: {
          // Tabular + lining figures throughout — every digit takes the
          // same advance width so count-up animations don't jitter and
          // columns of numbers in tables align. Kerning explicit (some
          // browsers default it off in CSS).
          fontFeatureSettings: '"tnum" 1, "lnum" 1, "kern" 1',
          fontVariantNumeric: 'tabular-nums lining-nums',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
          '&:hover': {
            borderColor: '#d1d5db',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          },
        },
      },
    },
  },
});

// ---------------------------------------------------------------------
// Documentation type scale
// ---------------------------------------------------------------------
//
// A conventional documentation scale — the sizes Stripe, Tailwind,
// Vercel and MDN docs all land on — rather than the ad-hoc fractional
// values (13.5, 12.75, 10.5) that accumulate when each component picks
// its own. Every value is a whole pixel and sits on one ladder:
//
//   12 · 14 · 16 · 18 · 20 · 32 · 44
//
// 16px body is the point of it. Documentation is read, not scanned like
// a dashboard, and 14px body copy is the single most common reason a
// manual feels cramped.
//
// Weights are limited to the ones actually loaded in `main.tsx`
// (Inter 400/500/600/700/800). Anything else — 650, say — is
// synthesised by the browser and renders inconsistently across
// platforms.
//
// First adopted by the User Guide (`pages/UserGuidePage.tsx`). Other
// long-form reading surfaces should move onto it; the analytics
// dashboards keep their denser scale, which is correct for tabular data.
export const docType = {
  /** 12px — uppercase eyebrows, badges, chips, image captions. */
  label: 12,
  /** 14px — secondary text: tips, glossary definitions, contents rows. */
  small: 14,
  /** 16px — body copy and list steps. The default for reading. */
  body: 16,
  /** 18px — lead paragraph under a title. */
  lead: 18,
  /** 20px — topic headings. */
  h3: 20,
  /** 32px desktop — chapter headings. */
  h2: 32,
  /** 44px desktop — the document title. */
  h1: 44,
} as const;

/**
 * Type scale for application UI — dense screens, not documentation.
 *
 * `docType` above is a READING scale (16px body, 44px title) built for
 * the User Guide. Admin tables, dialogs and toolbars need a tighter one,
 * and using the reading scale there makes everything look oversized.
 *
 * ── Why these numbers ─────────────────────────────────────────────────
 * Eight steps, all whole pixels, matching the scales used by the dense
 * product dashboards this app is modelled on (Linear, Stripe, Vercel).
 * Material 3 lands in the same place for its label/body/title roles.
 *
 * Whole pixels matter: half-pixel sizes land text on sub-pixel
 * boundaries, so a 12.5px label renders a shade blurrier than a 12px or
 * 13px one, and no two of them are reliably distinguishable anyway.
 * Nothing below 11px — smaller stops being readable at arm's length,
 * which is how a rep uses this on a laptop in a bar.
 */
export const uiType = {
  /** 11px — uppercase eyebrows and badges. Always with letter-spacing. */
  micro: 11,
  /** 12px — captions, helper text, timestamps. */
  caption: 12,
  /** 13px — dense secondary text: table rows, meta lines. */
  bodySm: 13,
  /** 14px — the default for UI text, buttons and inputs. */
  body: 14,
  /** 16px — card titles and emphasised rows. */
  subtitle: 16,
  /** 20px — section and dialog headings. */
  title: 20,
  /** 24px — page headings. */
  headline: 24,
  /** 32px — the one number or title a screen is built around. */
  display: 32,
} as const;

/** Line heights paired with `docType`. */
export const docLeading = {
  /** Display and headings. */
  tight: 1.2,
  /** Labels and single-line rows. */
  snug: 1.4,
  /** Secondary text. */
  normal: 1.6,
  /** Body copy — the documentation standard. */
  relaxed: 1.75,
} as const;

// Shared typography tokens for premium analytic-dashboard numerals.
// Use these instead of hard-coding fontFamily/feature settings in
// every component so the look stays consistent.
//
// Industry convention (Stripe, Linear, Vercel dashboards, MVP):
//   * Body + every numerical value: Inter, varying weights.
//   * Serif headlines (Playfair Display): page titles, section titles
//     ONLY. Numbers in Playfair lose readability and column alignment.
export const typographyTokens = {
  // Hero stat values — KPI tile big numbers, callout deltas, count-up
  // displays. Inter heavy weight, tight tracking, tabular figures
  // (inherited from the global CssBaseline rule).
  heroStat: {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.05,
  },
  // Mid-prominence numerical values (summary cards, KPI tiles on the
  // tablet/mobile grid, section sub-stats).
  midStat: {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.1,
  },
  // Table / row numbers (e.g. follow-up list values, account 9L totals).
  // Inter at 600 keeps it readable without competing with headlines.
  numCell: {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: 600,
  },
  // Pure UI labels — all-caps eyebrows, KPI tile labels, etc.
  eyebrow: {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: 700,
    fontSize: 10.5,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: colors.textMuted,
  },
  // Serif headlines — page titles, section titles, callout banners.
  // The ONE place Playfair Display belongs.
  pageTitle: {
    fontFamily: '"Playfair Display", "Palatino", Georgia, serif',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.1,
  },
  sectionTitle: {
    fontFamily: '"Inter", system-ui, sans-serif',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
    fontSize: 17,
  },
} as const;
